//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import { CampaignRequest } from '@apis/campaign-requests/shared/CampaignRequests';
import { CAMPAIGN_REQUEST_FORM_VALIDATION } from '@campaigns/constants/CampaignRequestFormValidationConstants';
import { CAMPAIGNS_TRACKING_ID } from '@campaigns/constants/CampaignsTrackingId';
import Button from '@components/atoms/button/Button';
import { DefaultButtonStyles } from '@components/atoms/button/buttonStyles';
import Icon from '@components/atoms/icon/Icon';
import LabeledCheckbox from '@components/atoms/labeled-checkbox/LabeledCheckbox';
import { CaptionErrorText } from '@components/atoms/text/Text';
import { FormFieldController } from '@components/form/field-controller/FormFieldController';
import { IDropdownSelectItem } from '@components/molecules/dropdown-select/DropdownSelect';
import SearchBar from '@components/molecules/search-bar/SearchBar';
import { Select } from '@components/primitives/select/Select';
import { BazaarvoiceClientId } from '@models/BazaarvoiceClientId';
import { isDefined, isTruthy, isUndefined } from '@utils/filterPredicates';

interface Props {
  retailerOptions: IDropdownSelectItem<BazaarvoiceClientId>[];
  shouldShowCheckbox?: boolean;
}

export const CampaignRequestRetailerField = ({
  retailerOptions,
  // TODO - PD-312744 Remove shouldShowCheckbox and all related logic once prepaid feature is fully launched to call clients
  shouldShowCheckbox = true,
}: Props) => {
  const { control, setValue, watch } = useFormContext<CampaignRequest>();
  const formValueRetailer = watch('targetClientId');

  const [isRetailerCheckboxChecked, setIsRetailerCheckboxChecked] = useState(
    isDefined(formValueRetailer) && formValueRetailer !== '',
  );
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    setIsRetailerCheckboxChecked(
      isDefined(formValueRetailer) && formValueRetailer !== '',
    );
  }, [formValueRetailer]);

  const isSubstring = (term: string, label: string) => {
    return label.toLocaleLowerCase().includes(term.toLocaleLowerCase());
  };

  return (
    <Container $hasCheckbox={shouldShowCheckbox}>
      {shouldShowCheckbox && (
        <LabeledCheckbox
          handleClick={(checked) => {
            setIsRetailerCheckboxChecked(checked);
            if (!checked) {
              setValue('targetClientId', '');
            } else {
              if (isUndefined(formValueRetailer) || formValueRetailer === '') {
                setValue('targetClientId', undefined);
              }
            }
          }}
          helpText='You may select a single retailer, who will receive all content generated as part of this campaign.'
          isChecked={isRetailerCheckboxChecked}
          label='Send content to a single retailer'
          trackingId={
            CAMPAIGNS_TRACKING_ID.CREATE_SAMPLING_CAMPAIGN_RETAILERS_CHECKBOX
          }
        />
      )}
      {(!shouldShowCheckbox || isRetailerCheckboxChecked) && (
        <FormFieldController
          control={control}
          name='targetClientId'
          rules={CAMPAIGN_REQUEST_FORM_VALIDATION.targetClientId}
        >
          {({ error, ...props }) => {
            const selectValue =
              isTruthy(props.value) && props.value !== ''
                ? props.value.toString()
                : undefined;

            const selectContent = (
              <>
                <Select
                  onOpenChange={(isOpen) => {
                    if (!isOpen) {
                      setSearchTerm('');
                    }
                  }}
                  onValueChange={(value) => {
                    props.onChange(BazaarvoiceClientId(value));
                  }}
                  value={selectValue}
                >
                  <Select.Trigger asChild>
                    <Button
                      buttonStyles={DefaultButtonStyles}
                      isDropdown={true}
                    >
                      <Select.Value placeholder='Select a retailer' />
                    </Button>
                  </Select.Trigger>
                  <StyledSelectContent align='start'>
                    <StyledSearchBar
                      autoFocus
                      onChange={setSearchTerm}
                      placeholder='Search'
                      trackingIdPrefix={
                        CAMPAIGNS_TRACKING_ID.CREATE_SAMPLING_CAMPAIGN_RETAILERS_SEARCH_BAR
                      }
                      value={searchTerm}
                      visuallyHiddenLabel='Search retailers'
                    />
                    {retailerOptions
                      .filter((option) => isDefined(option.value))
                      .map((option) => {
                        if (isUndefined(option.value)) {
                          return null;
                        }
                        const isSelected =
                          option.value.toString() === selectValue;
                        const isVisible =
                          isSubstring(searchTerm, option.label) || isSelected;

                        // The Select.Value component needs the selected item to be present in the
                        // content to render the label. We render all items and hide the
                        // ones that don't match the search term.
                        return (
                          <HiddenSelectItem
                            $hide={!isVisible}
                            key={option.label}
                            onSelect={() => {
                              props.onChange(option.value);
                              setSearchTerm('');
                            }}
                            value={option.value.toString()}
                          >
                            <Select.ItemText>{option.label}</Select.ItemText>
                            <Select.Indicator />
                          </HiddenSelectItem>
                        );
                      })}
                  </StyledSelectContent>
                </Select>
                {error && (
                  <ErrorText>
                    <StyledIcon name='error' />
                    <CaptionErrorText>{error.message}</CaptionErrorText>
                  </ErrorText>
                )}
              </>
            );
            return shouldShowCheckbox ? (
              <DropdownMenuContainer>{selectContent}</DropdownMenuContainer>
            ) : (
              selectContent
            );
          }}
        </FormFieldController>
      )}
    </Container>
  );
};

const Container = styled.div<{ $hasCheckbox: boolean }>`
  ${({ $hasCheckbox, theme }) => `
    margin-top: ${$hasCheckbox ? theme.spacings[3] : 0};
    gap: ${theme.spacings[2]};
  `}
  display: flex;
  flex-direction: column;
`;

const DropdownMenuContainer = styled.div`
  margin-left: 24px;
  max-width: 330px;
`;

const StyledSearchBar = styled(SearchBar)`
  padding: 0 ${({ theme }) => theme.spacings[2]};
`;

const StyledSelectContent = styled(Select.Content)`
  max-height: ${({ theme }) => theme.mediumMeasure};
  overflow-y: auto;
`;

const ErrorText = styled.div`
  display: flex;
  align-items: center;
`;

const HiddenSelectItem = styled(Select.Item)<{ $hide: boolean }>`
  display: ${({ $hide }) => ($hide ? 'none' : 'flex')};
`;

const StyledIcon = styled(Icon)(
  ({ theme }) => `
    margin-right: ${theme.spacings[1]};
    color: ${theme.colors.aliases.error};
    height: calc(${theme.spacings[1]} * 3);
    width: calc(${theme.spacings[1]} * 3);
`,
);
